import { useEffect, useRef, useState, type ReactElement } from 'react';

import { loginUrl } from '../api/auth.js';
import { useAuthStatus, useLogout } from '../hooks/useAuth.js';
import { useEscapeKey } from '../hooks/useEscapeKey.js';
import { LOBBY_PATH } from '../routing/paths.js';
import { useRoute } from '../routing/useRoute.js';
import { useCueSetting } from '../sync/usePollCycle.js';

const THEMES = ['system', 'light', 'dark'] as const;
type Theme = (typeof THEMES)[number];

const STORAGE_KEY = 'mixwire-theme';

const readStoredTheme = (): Theme => {
  const stored = localStorage.getItem(STORAGE_KEY);
  return THEMES.find((theme) => theme === stored) ?? 'system';
};

const GearIcon = (): ReactElement => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="size-5">
    <path
      d="M8.32 2.5h3.36l.5 2.06c.5.18.97.43 1.4.73l2-.68 1.68 2.9-1.6 1.4a5.9 5.9 0 0 1 0 1.58l1.6 1.4-1.68 2.9-2-.68c-.43.3-.9.55-1.4.73l-.5 2.06H8.32l-.5-2.06a5.86 5.86 0 0 1-1.4-.73l-2 .68-1.68-2.9 1.6-1.4a5.9 5.9 0 0 1 0-1.58l-1.6-1.4 1.68-2.9 2 .68c.43-.3.9-.55 1.4-.73l.5-2.06Z"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

// The one persistent, cross-page control; fixed in the corner rather than a
// header so it never implies an app shell wraps every page.
export const SettingsMenu = (): ReactElement => {
  const [theme, setTheme] = useState<Theme>(readStoredTheme);
  const { isCueEnabled, setCueEnabled } = useCueSetting();
  const authStatus = useAuthStatus();
  const logout = useLogout();
  const { navigate } = useRoute();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEscapeKey(() => {
    setOpen(false);
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', theme);
    }
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent): void => {
      const isInsideMenu =
        event.target instanceof Node && containerRef.current?.contains(event.target) === true;
      if (!isInsideMenu) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return (): void => {
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="fixed bottom-4 left-4 z-30">
      {open ? (
        <div className="bg-surface border-subtle shadow-raised rounded-card mb-2 border p-3">
          <fieldset className="flex flex-col gap-1">
            <legend className="text-muted mb-1.5 px-1 text-xs tracking-widest uppercase">
              Colour theme
            </legend>
            {THEMES.map((candidate) => (
              <label
                key={candidate}
                className={`relative cursor-pointer rounded-control px-3 py-1.5 text-sm capitalize transition-colors ${
                  theme === candidate
                    ? 'bg-accent-subtle text-content'
                    : 'text-muted hover:text-content'
                }`}
              >
                <input
                  type="radio"
                  name="theme"
                  value={candidate}
                  checked={theme === candidate}
                  onChange={() => {
                    setTheme(candidate);
                  }}
                  className="absolute inset-0 cursor-pointer appearance-none rounded-control"
                />
                <span className="pointer-events-none relative">{candidate}</span>
              </label>
            ))}
          </fieldset>

          <div className="border-subtle mt-2.5 border-t pt-2.5">
            <label className="text-muted hover:text-content flex cursor-pointer items-center gap-2.5 rounded-control px-3 py-1.5 text-sm">
              <input
                type="checkbox"
                checked={isCueEnabled}
                onChange={(event) => {
                  setCueEnabled(event.target.checked);
                }}
              />
              Live data cue
            </label>
          </div>

          {authStatus.data?.authenticated === false ? (
            <div className="border-subtle mt-2.5 border-t pt-2.5">
              <a
                href={loginUrl}
                className="text-muted hover:text-content block w-full rounded-control px-3 py-1.5 text-left text-sm"
              >
                Log in with Spotify
              </a>
            </div>
          ) : null}

          {authStatus.data?.authenticated === true ? (
            <div className="border-subtle mt-2.5 border-t pt-2.5">
              <button
                type="button"
                disabled={logout.isPending}
                onClick={() => {
                  logout.mutate(undefined, {
                    onSuccess: () => {
                      setOpen(false);
                      navigate(LOBBY_PATH);
                    },
                  });
                }}
                className="text-muted hover:text-content w-full rounded-control px-3 py-1.5 text-left text-sm disabled:opacity-50"
              >
                Log out
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        aria-label="Settings"
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className="bg-surface border-subtle text-content hover:bg-surface-hover shadow-card flex size-11 items-center justify-center rounded-pill border"
      >
        <GearIcon />
      </button>
    </div>
  );
};
